import { useNavigate } from "react-router-dom";
import FeatureLayout from "../../common/components/FeatureLayout";
import { ALL_FEATURES } from "../../config/features";
import { fetchGasData, fetchDieselData } from "../../api/wetmove-api-service";
import { useState, React } from "react";

const Home = () => {
  const navigate = useNavigate();
  const [features, setFeatures] = useState(ALL_FEATURES);
  const [wetMoveLoading, setWetMoveLoading] = useState(false);
  const [wetMoveLoaded, setWetMoveLoaded] = useState(false);

  const loadWetMoveSnapshot = async () => {
    // Don't reload if already loaded or currently loading
    if (wetMoveLoaded || wetMoveLoading) return;

    setWetMoveLoading(true);

    // Update description to show loading state
    setFeatures((prev) =>
      prev.map((f) =>
        f.isWetMoveSnapshot
          ? { ...f, description: "Loading WetMove data..." }
          : f
      )
    );

    try {
      const [gasRes, dieselRes] = await Promise.all([
        fetchGasData(),
        fetchDieselData(),
      ]);
      const gasData = Array.isArray(gasRes.data) ? gasRes.data : [];
      const dieselData = Array.isArray(dieselRes.data) ? dieselRes.data : [];

      // Find latest Gas entry where Argus and PlattsC > 0
      const latestGas = gasData
        .filter(
          (g) =>
            Number(g.wetMoveGas_Argus) > 0 &&
            Number(g.wetMoveGas_PlattsC) > 0 &&
            g.wetMoveGas_Date
        )
        .sort(
          (a, b) => new Date(b.wetMoveGas_Date) - new Date(a.wetMoveGas_Date)
        )[0];

      // Find latest Diesel entry where Argus and Platts > 0
      const latestDiesel = dieselData
        .filter(
          (d) =>
            Number(d.wetMoveDsl_Argus) > 0 &&
            Number(d.wetMoveDsl_Platts) > 0 &&
            d.wetMoveDsl_Date
        )
        .sort(
          (a, b) => new Date(b.wetMoveDsl_Date) - new Date(a.wetMoveDsl_Date)
        )[0];

      let wetMoveDescription = "No recent WetMove data found.";
      if (latestGas || latestDiesel) {
        wetMoveDescription =
          (latestGas
            ? `Gas (${latestGas.wetMoveGas_Date}): Argus $${latestGas.wetMoveGas_Argus}, Platts Chicago $${latestGas.wetMoveGas_PlattsC}`
            : "") +
          (latestDiesel
            ? `\nDiesel (${latestDiesel.wetMoveDsl_Date}): Argus $${latestDiesel.wetMoveDsl_Argus}, Platts $${latestDiesel.wetMoveDsl_Platts}`
            : "");
      }

      setFeatures((prev) =>
        prev.map((f) =>
          f.isWetMoveSnapshot ? { ...f, description: wetMoveDescription } : f
        )
      );
      setWetMoveLoaded(true);
    } catch (e) {
      setFeatures((prev) =>
        prev.map((f) =>
          f.isWetMoveSnapshot
            ? {
                ...f,
                description: "Failed to load WetMove data. Click to retry.",
              }
            : f
        )
      );
      setWetMoveLoaded(false);
    } finally {
      setWetMoveLoading(false);
    }
  };

  const handleFeatureClick = (route) => {
    // Load WetMove data when clicking on the WetMove snapshot card
    const clickedFeature = features.find((f) => f.route === route);
    if (clickedFeature?.isWetMoveSnapshot && !wetMoveLoaded) {
      loadWetMoveSnapshot();
    }
    navigate(route);
  };

  const handleFeatureHover = (route) => {
    // Load WetMove data when hovering over the WetMove snapshot card
    const hoveredFeature = features.find((f) => f.route === route);
    if (hoveredFeature?.isWetMoveSnapshot) {
      loadWetMoveSnapshot();
    }
  };

  // JSX render
  return (
    <FeatureLayout
      features={features}
      onFeatureClick={handleFeatureClick}
      onFeatureHover={handleFeatureHover}
      title="Home Dashboard"
    />
  );
};

export default Home;
